import type { RouteRecordRaw } from 'vue-router';

import type {
  SpartenConfig,
  SpartenConfigModules,
} from '@/features/Sparten/types';
import {
  KFZ_V1_SPARTEN,
  KFZ_MODE,
  kfzModeOf,
  kfzV2SparteOf,
  type KfzV2Sparte,
} from '@/features/Sparten/shared/kfz/kfzV2Switch';

export interface KfzV2SwitchResult {
  modules: SpartenConfigModules;
  aliasRedirects: RouteRecordRaw[];
}

/**
 * Im Modus `V2` läuft die V2-Config unter dem kanonischen Pfad. Der Sparten-Key bleibt
 * `campv2` – und damit auch der API-Pfad `/api/campv2/…`, dessen Alias im Backend den
 * V2-Container wählt. Nur Pfad und Anzeigename kommen aus der V1-Config, damit der Makler den
 * normalen Rechner sieht. Den Namen zu übernehmen hält beide Varianten automatisch synchron;
 * ein Abschneiden des „ (V2)"-Suffixes würde bei jeder Namensänderung brechen.
 */
const asCanonicalConfig = (
  v2Config: SpartenConfig,
  v1Config: SpartenConfig,
): SpartenConfig => ({
  ...v2Config,
  path: v1Config.path,
  props: { ...v2Config.props, name: v1Config.props.name },
});

/**
 * Leitet den Fake-Sparten-Pfad auf den kanonischen um. Nötig, weil mehrere Stellen
 * Frontend-URLs aus dem Sparten-Key bauen und im Modus `V2` sonst auf einen nicht mehr
 * gerouteten Pfad zeigen würden: `router/utils/refreshSession` (Redirect nach Re-Login),
 * `views/Layouts/Vorgaben.vue` (`resetInput`) und `utils/generateURLs` (`generateVglUrl`).
 *
 * Der wiederholbare Parameter deckt auch den nackten Pfad `/campv2` mit ab.
 */
const aliasRedirectFor = (
  v2Sparte: KfzV2Sparte,
  canonicalPath: string,
): RouteRecordRaw => ({
  name: `${v2Sparte}.alias-redirect`,
  path: `/${v2Sparte}/:pathMatch(.*)*`,
  redirect: (to) => {
    const { pathMatch } = to.params;
    const rest = Array.isArray(pathMatch) ? pathMatch.join('/') : pathMatch;

    return {
      path:
        rest === undefined || rest === ''
          ? canonicalPath
          : `${canonicalPath}/${rest}`,
      query: to.query,
      hash: to.hash,
    };
  },
});

/**
 * Löst den KFZ-V2-Schalter auf, bevor `createSpartenRoutes` die Module filtert. Arbeitet
 * nicht-mutierend: `spartenConfigModules` ist ein Singleton, das auch
 * `getCurrentSpartenConfig()` und die Store-/Schema-Registries lesen.
 */
export const applyKfzV2Switch = (
  modules: SpartenConfigModules,
): KfzV2SwitchResult => {
  const effective: Partial<Record<string, SpartenConfig>> = { ...modules };
  const aliasRedirects: RouteRecordRaw[] = [];

  KFZ_V1_SPARTEN.forEach((v1Sparte) => {
    const v2Sparte = kfzV2SparteOf(v1Sparte);
    const v1Config = effective[v1Sparte];
    const v2Config = effective[v2Sparte];

    if (v1Config === undefined || v2Config === undefined) {
      return;
    }

    switch (kfzModeOf(v1Sparte)) {
      case KFZ_MODE.V1:
        delete effective[v2Sparte];
        break;

      case KFZ_MODE.V2:
        delete effective[v1Sparte];
        effective[v2Sparte] = asCanonicalConfig(v2Config, v1Config);
        aliasRedirects.push(aliasRedirectFor(v2Sparte, v1Config.path));
        break;

      case KFZ_MODE.PARALLEL:
      default:
        break;
    }
  });

  return {
    modules: effective as SpartenConfigModules,
    aliasRedirects,
  };
};
